import { Stack } from '@mui/material';
import Button from '@mui/material/Button';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import EndorsementsApi from '../../../api/EndorsementsApi';
import {
	AutofillOperations,
	AutofillOperationTypes,
} from '../../../api/IntegrationsApi';
import RenewalsApi from '../../../api/RenewalsApi';
import { AuthData } from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import {
	EndorsementData,
	withEndorsementContext,
} from '../../../context/EndorsementProvider';
import { ErrorData } from '../../../context/ErrorProvider';
import { CLONE_ERROR_REGEX } from '../../../data/constants';
import {
	ACCEPT_ENDORSEMENT_CONFLICT_MSG,
	ACCEPT_ENDORSEMENT_LABEL,
	ACCEPT_ENDORSEMENT_MSG,
	CLONE_ENDORSEMENT_LABEL,
	CLONE_ENDORSEMENT_UNSAVED_CHANGES_CANCEL_TEXT,
	CLONE_ENDORSEMENT_UNSAVED_CHANGES_CONFIRM_TEXT,
	CLONE_ENDORSEMENT_UNSAVED_CHANGES_MSG,
	DISCARD_ENDORSEMENT_LABEL,
	DISCARD_ENDORSEMENT_MSG,
	ENDORSEMENT_AUTOFILL_UPDATE_FAILURE,
	ENDORSEMENT_AUTOFILL_UPDATE_SUCCESS,
	ENDORSEMENT_CLONE_CONFLICT_FAILURE,
	ENDORSEMENT_CLONE_CONFLICT_UNKNOWN_FAILURE,
	ENDORSEMENT_CLONE_SUCCESS,
	ENDORSEMENT_SAVE_TEXT,
	ENDORSEMENT_UPDATE_FAILURE,
	ENDORSEMENT_UPDATE_SUCCESS,
	INVALIDATE_ENDORSEMENT_CONFLICT_MSG,
	INVALIDATE_ENDORSEMENT_LABEL,
	INVALIDATE_ENDORSEMENT_MSG,
	ISSUE_ENDORSEMENT_LABEL,
	ISSUE_ENDORSEMENT_MSG,
	PROCEED_MSG,
	QUOTE_ENDORSEMENT_LABEL,
	QUOTE_ENDORSEMENT_MSG,
} from '../../../data/labelsAndMessages';
import { EndorsementResponse } from '../../../types/endorsements';
import { RenewalResponse } from '../../../types/renewals';
import { isAutofillConfigured } from '../../../util/util';
import DropdownButton, { HiddenActionItem } from '../../DropdownButton';
import { errorToast, successToast } from '../../Toast';
import ActionConfirmationModal from '../ActionConfirmationModal';
import RunAutofillButtonAndModal from '../RunAutofillButtonModal';
import { endorsementHasUnsavedChanges } from '../util_sharedEndorsementAndRenewal';

interface Props {
	endorsementData: EndorsementData;
	authData: AuthData;
	errorData: ErrorData;
}

const EndorsementActions = (props: Props) => {
	const configData = useConfigContext();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields, isDirty } = formState;
	const { authData, endorsementData, errorData } = props;
	const {
		endorsementUpdateObj,
		quoteEndorsement,
		acceptEndorsement,
		issueEndorsement,
		invalidateEndorsement,
		discardEndorsement,
		policy,
		saveFastForm,
		setLoading,
		cloneEndorsement,
	} = endorsementData;
	const { hasPermission } = authData;
	const { state } = endorsementUpdateObj;
	const navigate = useNavigate();

	const { data: renewals } = useQuery({
		queryKey: ['renewals', policy.locator, authData.auth.authorizationToken],
		enabled: hasPermission('renewal.fetch'),
		queryFn: () =>
			RenewalsApi.getRenewalsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const { data: endorsements } = useQuery({
		queryKey: [
			'endorsements',
			policy.locator,
			authData.auth.authorizationToken,
		],
		queryFn: () =>
			EndorsementsApi.getEndorsementsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
	});

	const quotedEndorsements = endorsements?.data.filter(
		(endorsement: EndorsementResponse) =>
			endorsement.state === 'quoted' &&
			endorsement.locator !== endorsementUpdateObj.locator,
	);
	const quotedRenewals = renewals?.data.filter(
		(renewal: RenewalResponse) => renewal.state === 'quoted',
	);
	const willInvalidateOtherQuotes =
		!!quotedEndorsements?.length || !!quotedRenewals?.length;

	// https://socotra.atlassian.net/browse/SOCA-7817
	// If a user does not have access to the endorsement.fetch permission, there will be a chance other quotes will be invalidated (but we don't know for sure)
	const mayInvalidateOtherQuotes =
		!willInvalidateOtherQuotes && !authData.hasPermission('renewal.fetch');

	const hasUnsavedChanges = endorsementHasUnsavedChanges(
		endorsementData.endorsementResponse,
		endorsementUpdateObj,
		isDirty,
		dirtyFields,
		endorsementData.productConfig,
		policy,
	);
	const autofillEnabled = isAutofillConfigured(
		policy.productName,
		AutofillOperations.ENDORSEMENT,
		AutofillOperationTypes.UPDATE,
		configData?.dataAutofillSequences ?? [],
	);

	const onUpdate = async () => {
		try {
			setLoading(true);
			await saveFastForm(getValues(), dirtyFields, reset, {
				runAutofill: false,
			});
			successToast(ENDORSEMENT_UPDATE_SUCCESS());
		} catch (e) {
			errorToast(ENDORSEMENT_UPDATE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	const onRunAutofill = async () => {
		try {
			setLoading(true);
			await saveFastForm(getValues(), dirtyFields, reset, {
				runAutofill: true,
			});
			successToast(ENDORSEMENT_AUTOFILL_UPDATE_SUCCESS());
		} catch (e) {
			errorToast(ENDORSEMENT_AUTOFILL_UPDATE_FAILURE());
		} finally {
			setLoading(false);
		}
	};

	const onQuote = async () => {
		let success = false;
		try {
			success = await quoteEndorsement(getValues(), dirtyFields, reset, {});
		} catch (e) {
			setLoading(false);
		}
		if (success) {
			navigate(
				`/policy/${endorsementUpdateObj.policyLocator}/endorsements/${endorsementUpdateObj.locator}/price-breakdown`,
			);
		}
	};

	const onAccept = async (isDraft?: boolean) => {
		let success = false;
		try {
			success = await acceptEndorsement(
				getValues(),
				dirtyFields,
				reset,
				{},
				isDraft,
			);
		} catch (e) {
			setLoading(false);
		}
		if (success) {
			navigate(
				`/policy/${endorsementUpdateObj.policyLocator}/endorsements/${endorsementUpdateObj.locator}/price-breakdown`,
			);
		}
	};

	const onIssue = async (isDraft?: boolean) => {
		let success = false;
		try {
			success = await issueEndorsement(
				getValues(),
				dirtyFields,
				reset,
				{},
				isDraft,
			);
		} catch (e) {
			setLoading(false);
		}

		if (success) {
			navigate(
				`/policy/${endorsementUpdateObj.policyLocator}/endorsements/${endorsementUpdateObj.locator}/price-breakdown`,
			);
		}
	};

	const onInvalidate = async () => {
		await invalidateEndorsement();
		navigate(`/policy/${endorsementUpdateObj.policyLocator}/endorsements`);
	};

	const onDiscard = async () => {
		await discardEndorsement();
		navigate(`/policy/${endorsementUpdateObj.policyLocator}/endorsements`);
	};

	const onClone = async () => {
		try {
			setLoading(true);
			const clonedEndorsement = await cloneEndorsement();
			successToast(ENDORSEMENT_CLONE_SUCCESS());
			navigate(
				`/policy/${clonedEndorsement.policyLocator}/endorsements/${clonedEndorsement.locator}`,
			);
		} catch (e) {
			const errorMessage = e?.response?.data?.message;
			if (CLONE_ERROR_REGEX.test(errorMessage)) {
				errorData.handleErrorResponse(e, ENDORSEMENT_CLONE_CONFLICT_FAILURE());
			} else {
				errorData.handleErrorResponse(
					e,
					ENDORSEMENT_CLONE_CONFLICT_UNKNOWN_FAILURE(),
				);
			}
		} finally {
			setLoading(false);
		}
	};

	const getCloneEndorsementAction = (): HiddenActionItem => {
		if (state === 'application' && hasUnsavedChanges) {
			return {
				label: CLONE_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={CLONE_ENDORSEMENT_LABEL()}
						message={CLONE_ENDORSEMENT_UNSAVED_CHANGES_MSG()}
						confirmText={CLONE_ENDORSEMENT_UNSAVED_CHANGES_CONFIRM_TEXT()}
						cancelText={CLONE_ENDORSEMENT_UNSAVED_CHANGES_CANCEL_TEXT()}
						dropdownItem
						onConfirm={onClone}
					/>
				),
			};
		}

		return {
			label: CLONE_ENDORSEMENT_LABEL(),
			callback: onClone,
		};
	};

	const acceptMessage = willInvalidateOtherQuotes
		? ACCEPT_ENDORSEMENT_CONFLICT_MSG('will')
		: mayInvalidateOtherQuotes
		? ACCEPT_ENDORSEMENT_CONFLICT_MSG('may')
		: ACCEPT_ENDORSEMENT_MSG();

	const invalidateMessage = willInvalidateOtherQuotes
		? INVALIDATE_ENDORSEMENT_CONFLICT_MSG('will')
		: mayInvalidateOtherQuotes
		? INVALIDATE_ENDORSEMENT_CONFLICT_MSG('may')
		: INVALIDATE_ENDORSEMENT_MSG();

	const renderDraftActions = () => {
		const hiddenOptions: HiddenActionItem[] = [];

		hasPermission('endorsement.accept') &&
			hiddenOptions.push({
				label: ACCEPT_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={ACCEPT_ENDORSEMENT_LABEL()}
						message={acceptMessage}
						confirmText={ACCEPT_ENDORSEMENT_LABEL()}
						dropdownItem
						onConfirm={() => onAccept(true)}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				),
			});
		hasPermission('endorsement.issue') &&
			hiddenOptions.push({
				label: ISSUE_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={ISSUE_ENDORSEMENT_LABEL()}
						message={ISSUE_ENDORSEMENT_MSG()}
						confirmText={ISSUE_ENDORSEMENT_LABEL()}
						dropdownItem
						onConfirm={() => onIssue(true)}
					/>
				),
			});
		hasPermission('endorsement.discard') &&
			hiddenOptions.push({
				label: DISCARD_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						label={DISCARD_ENDORSEMENT_LABEL()}
						message={DISCARD_ENDORSEMENT_MSG()}
						confirmText={DISCARD_ENDORSEMENT_LABEL()}
						dropdownItem
						onConfirm={onDiscard}
					/>
				),
			});
		hasPermission('endorsement.create') &&
			hiddenOptions.push(getCloneEndorsementAction());

		return (
			<Stack direction='row' spacing={1}>
				{autofillEnabled && hasPermission('endorsement.update') ? (
					<RunAutofillButtonAndModal
						onConfirm={onRunAutofill}
						disabled={false}
					/>
				) : null}

				{hasPermission('endorsement.update') && (
					<Button
						color='primary'
						variant='contained'
						disabled={!hasUnsavedChanges}
						onClick={onUpdate}
					>
						{ENDORSEMENT_SAVE_TEXT()}
					</Button>
				)}
				{hasPermission('endorsement.quote') && (
					<ActionConfirmationModal
						label={QUOTE_ENDORSEMENT_LABEL()}
						message={QUOTE_ENDORSEMENT_MSG()}
						confirmText={QUOTE_ENDORSEMENT_LABEL()}
						onConfirm={onQuote}
					/>
				)}
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	const renderQuotedActions = () => {
		const hiddenOptions: HiddenActionItem[] = [];
		hasPermission('endorsement.issue') &&
			hiddenOptions.push({
				label: ISSUE_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={ISSUE_ENDORSEMENT_LABEL()}
						message={ISSUE_ENDORSEMENT_MSG()}
						confirmText={ISSUE_ENDORSEMENT_LABEL()}
						onConfirm={onIssue}
					/>
				),
			});
		hasPermission('endorsement.invalidate') &&
			hiddenOptions.push({
				label: INVALIDATE_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={INVALIDATE_ENDORSEMENT_LABEL()}
						message={INVALIDATE_ENDORSEMENT_MSG()}
						confirmText={INVALIDATE_ENDORSEMENT_LABEL()}
						onConfirm={onInvalidate}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				),
			});
		hasPermission('endorsement.discard') &&
			hiddenOptions.push({
				label: DISCARD_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={DISCARD_ENDORSEMENT_LABEL()}
						message={DISCARD_ENDORSEMENT_MSG()}
						confirmText={DISCARD_ENDORSEMENT_LABEL()}
						onConfirm={onDiscard}
					/>
				),
			});
		hasPermission('endorsement.create') &&
			hiddenOptions.push(getCloneEndorsementAction());

		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('endorsement.accept') && (
					<ActionConfirmationModal
						label={ACCEPT_ENDORSEMENT_LABEL()}
						message={acceptMessage}
						confirmText={ACCEPT_ENDORSEMENT_LABEL()}
						onConfirm={onAccept}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				)}
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	const renderAcceptedActions = () => {
		const hiddenOptions: HiddenActionItem[] = [];
		hasPermission('endorsement.invalidate') &&
			hiddenOptions.push({
				label: INVALIDATE_ENDORSEMENT_LABEL(),
				callback: null,
				component: (
					<ActionConfirmationModal
						dropdownItem
						label={INVALIDATE_ENDORSEMENT_LABEL()}
						message={invalidateMessage}
						confirmText={INVALIDATE_ENDORSEMENT_LABEL()}
						onConfirm={onInvalidate}
					>
						{willInvalidateOtherQuotes || mayInvalidateOtherQuotes
							? PROCEED_MSG()
							: null}
					</ActionConfirmationModal>
				),
			});
		hasPermission('endorsement.create') &&
			hiddenOptions.push(getCloneEndorsementAction());
		return (
			<Stack direction='row' spacing={1}>
				{hasPermission('endorsement.issue') && (
					<ActionConfirmationModal
						label={ISSUE_ENDORSEMENT_LABEL()}
						message={ISSUE_ENDORSEMENT_MSG()}
						confirmText={ISSUE_ENDORSEMENT_LABEL()}
						onConfirm={onIssue}
					/>
				)}
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	const renderDefaultActions = () => {
		const hiddenOptions: HiddenActionItem[] = [];
		hasPermission('endorsement.create') &&
			hiddenOptions.push(getCloneEndorsementAction());
		return (
			<Stack direction='row' spacing={1}>
				<DropdownButton actions={hiddenOptions} />
			</Stack>
		);
	};

	if (!hasPermission('endorsement.update')) {
		return null;
	}

	if (state === 'application') {
		return renderDraftActions();
	} else if (state === 'quoted') {
		return renderQuotedActions();
	} else if (state === 'accepted') {
		return renderAcceptedActions();
	} else {
		return renderDefaultActions();
	}
};

export default withEndorsementContext<Props>(EndorsementActions);
